import { css } from '@emotion/react';
import React from 'react';

import Loading from '$/components/Loading';
import SimpleModal from '$/components/ui/SimpleModal';
import { useThemeContext } from '$/components/ui/ThemeContext';

import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useMe } from '$/hooks/useMe';

import { hasPermission } from '$/utils/hasPermission';
import { rolesPermissionsMatrix } from '$/utils/pathConfig';

import { UpdateAddOnType, useAddOnsContext } from './AddOnsContext';
import { useUpdateAddOn } from './useUpdateAddOn';
import Button from '$/components/ui/Button';

export const UpdateAddOnModal: React.FC = () => {
  const me = useMe();
  const etheme = useThemeContext();
  const { modal, closeModal } = useAddOnsContext();
  const { submitting, updateAddOn, error } = useUpdateAddOn();

  const addOn = modal?.addOn;
  const type = modal?.type;
  const canUpdateAddon = hasPermission(rolesPermissionsMatrix.profilePlan.path, me);

  const showModal = type !== UpdateAddOnType.Resubscribe;

  const onSubmit = React.useCallback(async (): Promise<void> => {
    if (!addOn) return;
    if (!canUpdateAddon) return;
    await updateAddOn({
      type: addOn.type,
      quantity: 1,
      deleted: type === UpdateAddOnType.Unsubscribe,
    });
    closeModal();
    modal?.onAddSuccess?.();
  }, [addOn, canUpdateAddon, closeModal, modal, type, updateAddOn]);

  const addOnHasChanged = useHasChangedShallow([addOn]);
  React.useEffect(() => {
    if (showModal || !addOnHasChanged || !addOn) return;
    (async function (): Promise<void> {
      await updateAddOn({
        type: addOn.type,
        quantity: 1,
      });
      closeModal();
    })();
  }, [showModal, addOnHasChanged, onSubmit, addOn, updateAddOn, type, closeModal]);

  if (!addOn || !type) return null;

  if (!showModal) {
    return <Loading className='w-8 h-8' />;
  }

  const freeUntil = addOn.getFreeUntil(me);

  return (
    <SimpleModal
      closeOnClickOutside={false}
      title={addOn.getModalTitle(type)}
      className='md:min-w-40e'
      onCancel={closeModal}
      cancel={<Button className='mr-4'>Cancel</Button>}
      submit={
        <Button.submit
          theme={etheme.controls.button.action}
          disabled={!canUpdateAddon}
          css={css`
            font-weight: 400;
          `}
          onClick={(): void => void onSubmit()}
          submitting={submitting}
        >
          <span className='flex px-1/4e'>{addOn.getCtaButtonText(type)}</span>
        </Button.submit>
      }
    >
      <p className='mb-1e border-gray-400'>
        {addOn.getModalMessage(type, me.team.billingCycle, freeUntil)}
      </p>
      {!canUpdateAddon && (
        <p className='text-color-error mb-2'>You need to be an admin to access this function.</p>
      )}
      {error && (
        <p className='text-color-error mb-2'>
          {error} If this problem persists, please contact{' '}
          <a href='mailto://support@ewebinar.com'>support@ewebinar.com</a>.
        </p>
      )}
    </SimpleModal>
  );
};
